import { Diagnostic } from '_store/api/types';

import { RsuStatusCode } from './devicesTypes';

/** Matches `yellowRsu.png` map marker (deeper gold vs CSS `yellow`). */
export const RSU_WARNING_COLOR = '#f4c911';

/**
 * Determines the RSU status code based on diagnostic information.
 *
 * RED/ERROR if any of the following are true:
 * - Free Disk space: free/total < 0.2 (less than 20%)
 * - GPS fix: navFixIsValid === false
 * - CPU temperature: cpuTemp > 70
 *
 * ORANGE/WARNING if any of the following are true AND no ERROR condition:
 * - Free Disk space: 0.2 <= free/total < 0.4 (between 20% and 40%)
 * - CPU temperature: 60 < cpuTemp <= 70
 *
 * GREEN/UP otherwise
 *
 * @param diagnostic - The diagnostic object from the API state
 * @returns The appropriate RsuStatusCode based on diagnostic status
 */
export const getRsuStatusFromDiagnostic = (diagnostic: Diagnostic | undefined): RsuStatusCode | undefined => {
  if (!diagnostic) {
    return undefined;
  }

  const { statusDetails } = diagnostic;
  if (!statusDetails) {
    return undefined;
  }

  const { diskFree, diskTotal, navFixIsValid, cpuTemp } = statusDetails;

  // Calculate disk ratio (handle division by zero)
  const diskRatio = diskTotal > 0 ? diskFree / diskTotal : 1;

  // Check ERROR conditions (red)
  const isDiskError = diskRatio < 0.2;
  const isGpsError = navFixIsValid === false;
  const isCpuError = cpuTemp > 70;

  if (isDiskError || isGpsError || isCpuError) {
    return RsuStatusCode.ERROR;
  }

  // Check WARNING conditions (orange)
  const isDiskWarning = diskRatio >= 0.2 && diskRatio < 0.4;
  const isCpuWarning = cpuTemp > 60 && cpuTemp <= 70;

  if (isDiskWarning || isCpuWarning) {
    return RsuStatusCode.WARNING;
  }

  // All conditions are good
  return RsuStatusCode.UP;
};

export const parseUptimeHours = (uptime: string): number => {
  if (!uptime) return 0;

  let totalHours = 0;

  // Cerca i giorni nel formato "X days"
  const daysMatch = uptime.match(/(\d+)\s+days?/);
  if (daysMatch) {
    totalHours += parseInt(daysMatch[1], 10) * 24;
  }

  // Cerca le ore nel formato "X hours"
  const hoursMatch = uptime.match(/(\d+)\s+hours?/);
  if (hoursMatch) {
    totalHours += parseInt(hoursMatch[1], 10);
  }

  return totalHours;
};

/**
 * Display value for RSU vendor. When the backend sends "-", uses RSU_VENDOR_DEFAULT env var if set.
 */
export const getDisplayVendor = (vendor: string | undefined): string => {
  if (vendor === '-') {
    return process.env.RSU_VENDOR_DEFAULT || '-';
  }
  return vendor ?? '-';
};

const SECONDS_PER_DAY = 24 * 60 * 60; // 86400

/** Returns all dates from minDate to maxDate (inclusive) as YYYY-MM-DD strings. */
function getDatesInRange(minDate: string, maxDate: string): string[] {
  const dates: string[] = [];
  const start = new Date(minDate);
  const end = new Date(maxDate);
  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

/**
 * Spreads a device's daily downtime so that no single day exceeds 24h.
 * When a day has downtime > 86400 seconds, the excess is moved to the following day(s)
 * until exhausted or the date range ends. Totals are recomputed after spreading.
 */
function normalizeDeviceDays(days: { date: string; downtime: number }[]): {
  days: { date: string; downtime: number }[];
  totalDowntimeSeconds: number;
  totalDowntimeHours: number;
} {
  if (!days?.length) {
    return { days: [], totalDowntimeSeconds: 0, totalDowntimeHours: 0 };
  }

  const sortedDays = [...days].sort((a, b) => a.date.localeCompare(b.date));
  // First day that had >24h before spread (API guarantees at most one per RSU) — this is the day to swap with last
  const overflowStartDate = sortedDays.find((d) => d.downtime > SECONDS_PER_DAY)?.date ?? null;

  let carry = 0;
  const normalized = sortedDays.map((day) => {
    const total = day.downtime + carry;
    carry = Math.max(0, total - SECONDS_PER_DAY);
    const assigned = Math.min(total, SECONDS_PER_DAY);
    return { date: day.date, downtime: assigned };
  });

  if (carry > 0 && normalized.length > 0) {
    normalized[normalized.length - 1].downtime += carry;
  }

  // If the spread reached the last day, swap with the day that had >24h (partial first day of that outage)
  const lastDayDowntime = normalized.length > 0 ? normalized[normalized.length - 1].downtime : 0;
  const overflowDayIndex =
    overflowStartDate != null
      ? normalized.findIndex((d) => d.date === overflowStartDate)
      : -1;
  if (
    lastDayDowntime > 0 &&
    overflowDayIndex >= 0 &&
    overflowDayIndex < normalized.length - 1
  ) {
    const overflowDayValue = normalized[overflowDayIndex].downtime;
    normalized[overflowDayIndex].downtime = lastDayDowntime;
    normalized[normalized.length - 1].downtime = overflowDayValue;
  }

  const totalDowntimeSeconds = normalized.reduce((sum, d) => sum + d.downtime, 0);
  const totalDowntimeHours = totalDowntimeSeconds / 3600;

  return { days: normalized, totalDowntimeSeconds, totalDowntimeHours };
}

/**
 * Normalizes RSU downtimes segments API response so that no single day has more than 24h
 * of downtime per device. Excess is spread to subsequent days. Segment and device totals
 * are recomputed after spreading.
 * When requestStart/requestEnd are provided (e.g. from the API request), the segment date
 * range is extended to that range so missing subsequent days are created even when the API
 * returns only a subset of devices (e.g. when filtering by RSU).
 */
export function normalizeRsuDowntimesSegmentsResponse(
  response: {
    segments?: Array<{
      devices?: Array<{
        days: { date: string; downtime: number }[];
        name?: string;
        rsuId?: string;
        ipAddress?: string;
        stationId?: string;
        totalDowntimeHours?: number;
        totalDowntimeSeconds?: number;
      }>;
      roadSegmentId?: string;
      roadSegmentName?: string;
      totalDevicesDowntimeHours?: number;
      totalDevicesDowntimeSeconds?: number;
    }>;
    timeFrameHours?: number;
  } | null | undefined,
  requestStart?: string,
  requestEnd?: string,
): typeof response {
  if (!response?.segments?.length) {
    return response;
  }

  const segments = response.segments.map((segment) => {
    if (!segment.devices?.length) {
      return segment;
    }

    // Use request date range when provided (e.g. when filtering by RSU) so we create
    // subsequent days up to the end of the range; otherwise derive from response
    const allSegmentDates = segment.devices.flatMap((device) =>
      (device.days ?? []).map((d) => d.date),
    );
    const responseMin =
      allSegmentDates.length > 0
        ? allSegmentDates.reduce((a, b) => (a < b ? a : b))
        : null;
    const responseMax =
      allSegmentDates.length > 0
        ? allSegmentDates.reduce((a, b) => (a > b ? a : b))
        : null;
    const segmentMinDate = requestStart && requestEnd ? requestStart : responseMin;
    const segmentMaxDate = requestStart && requestEnd ? requestEnd : responseMax;
    const allDatesInRange =
      segmentMinDate && segmentMaxDate ? getDatesInRange(segmentMinDate, segmentMaxDate) : [];

    const dayMap = (days: { date: string; downtime: number }[]) =>
      new Map(days.map((d) => [d.date, d.downtime]));

    let segmentTotalSeconds = 0;
    const devices = segment.devices.map((device) => {
      const existingMap = dayMap(device.days ?? []);
      const fullDays = allDatesInRange.map((date) => ({
        date,
        downtime: existingMap.get(date) ?? 0,
      }));
      const { days, totalDowntimeSeconds, totalDowntimeHours } = normalizeDeviceDays(fullDays);
      segmentTotalSeconds += totalDowntimeSeconds;
      return {
        ...device,
        days,
        totalDowntimeSeconds,
        totalDowntimeHours,
      };
    });

    const totalDevicesDowntimeHours = segmentTotalSeconds / 3600;
    const totalDevicesDowntimeSeconds = segmentTotalSeconds;

    return {
      ...segment,
      devices,
      totalDevicesDowntimeHours,
      totalDevicesDowntimeSeconds,
    };
  });

  return {
    ...response,
    segments,
  };
}
